import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';

import { API } from '../lib/api';
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 1500;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', attempts: 0, plan: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', attempts: 0, plan: null, error: 'Missing session' });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;

      try {
        const saved = JSON.parse(localStorage.getItem('gynovate_signup') || 'null');
        if (saved?.phone) {
          const r = await axios.get(`${API}/api/plan/${saved.phone}`);
          if (r.data?.active) {
            setState({ status: 'success', attempts: attemptsRef.current, plan: r.data, error: null });
            return;
          }
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', attempts: attemptsRef.current, plan: null, error: null });
          return;
        }
        setState({ status: 'polling', attempts: attemptsRef.current, plan: null, error: null });
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        setState({ status: 'error', attempts: attemptsRef.current, plan: null, error: err?.message || 'Error' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const pkgLabel = state.plan?.plan === 'pro' ? 'Gynovate AI/SI Pro' : 'Gynovate AI/SI';

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: 'linear-gradient(135deg, #0f0509 0%, #1a0a10 50%, #0f0509 100%)' }}>
      <div className="max-w-md w-full bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-10 text-center"
           data-testid="payment-success-card">
        {state.status === 'polling' && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-5 text-raspberry-400 animate-spin" />
            <h1 className="text-2xl font-black text-white mb-2">Confirming payment…</h1>
            <p className="text-white/60 text-sm">Activating your plan ({state.attempts}/{MAX_ATTEMPTS})</p>
          </>
        )}

        {state.status === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto mb-5 text-emerald-400" data-testid="payment-success-icon" />
            <h1 className="text-3xl font-black text-white mb-2">Payment Successful!</h1>
            <p className="text-white/70 text-sm mb-1">Your <b>{pkgLabel}</b> plan is now active.</p>
            <p className="text-white/50 text-xs mb-6">A receipt has been sent to your email.</p>
            <div className="rounded-xl border border-white/10 p-4 mb-6 text-left text-xs space-y-1.5">
              <div className="flex justify-between"><span className="text-white/50">Plan</span><span className="text-white font-bold">{pkgLabel}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Status</span><span className="text-emerald-400 font-bold">Active</span></div>
              {state.plan?.expires_at && (
                <div className="flex justify-between">
                  <span className="text-white/50">Expires</span>
                  <span className="text-white font-bold">{new Date(state.plan.expires_at).toLocaleDateString('en-IN')}</span>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/chat')}
              className="w-full py-3.5 rounded-xl bg-raspberry-600 text-white text-sm font-bold hover:bg-raspberry-700 transition-all flex items-center justify-center gap-2"
              data-testid="payment-goto-chat-btn"
            >
              Go to Gynovate AI/SI
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {(state.status === 'timeout' || state.status === 'error') && (
          <>
            <AlertTriangle className="w-14 h-14 mx-auto mb-5 text-amber-400" />
            <h1 className="text-2xl font-black text-white mb-2">
              {state.status === 'timeout' ? 'Still Processing' : 'Something went wrong'}
            </h1>
            <p className="text-white/60 text-sm mb-6">
              {state.status === 'timeout'
                ? 'Your payment is taking longer than usual. Check your email — if you see a receipt, your plan is active.'
                : 'Please try again or contact support if the amount was deducted.'}
            </p>
            <Link to="/" className="block w-full py-3 rounded-xl border border-white/15 text-white text-sm font-bold">
              Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
